import { useEffect, useId, useMemo, useState, type ReactNode } from 'react'
import { Button, Chip } from '@heroui/react'
import { api, ApiError, type LayoutAsset, type StoredLayout } from '../../lib/api'
import { explain } from '../../lib/staffErrors'
import { Announcer, chimesCalls, MAX_CHIME_SECONDS, MAX_SOUND_BYTES, readsCalls, soundSeconds, speechSource } from '../../lib/voice'
import { FilePicker } from '../../ui/FilePicker'
import { Icon } from '../../ui/icons'
import { CALL_PAGE_DEFAULT_SECONDS, CALL_PAGE_SECONDS_CHOICES } from '../../lib/callPage'
import { DESK_SWITCHES, withVerb } from '../../lib/deskVerbs'
import { SETTING_FIELDS } from '../../lib/equipment'
import type { StaffKey } from '../../lib/staffText'
import type { CallPageSettings, PrinterSettings, ProfileUnitKind, UnitSettings, VoiceSettings } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { Check, LANGUAGES, Num } from '../parts'
import { designLanguages } from '../../layout/designLanguages'
import { nativeName } from '../../lib/i18n/customer'
import { useCustomerLanguages } from '../../lib/i18n/useCustomerLanguages'
import { Choices, Pick, Segmented } from '../setupKit'

export type SettingField = keyof UnitSettings
export type InheritedFrom = 'default' | 'profile'

/** What a group holds once it is someone's own, when the level above sent nothing. */
function blankOf(field: SettingField, kind: ProfileUnitKind): NonNullable<UnitSettings[SettingField]> {
  switch (field) {
    case 'withheld':
      return []
    case 'layoutId':
    case 'ticketLayoutId':
      return ''
    case 'voice':
      return kind === 'desk_display' ? { calls: false } : {}
    default:
      return {}
  }
}

const TITLE: Record<SettingField, StaffKey> = {
  withheld: 'eq.sheet.switches',
  printer: 'eq.sheet.printer',
  layoutId: 'eq.sheet.design',
  ticketLayoutId: 'eq.sheet.ticket',
  voice: 'eq.sheet.sound',
  callPage: 'eq.sheet.fullScreen',
}

/**
 * A unit's settings, one block per setting group of its kind. A block shows the
 * inherited value until changed, then holds the level's own, taken whole (R10),
 * with Reset to inherit again. inheritedFrom is one source for every group, or one per group.
 */
export function SettingsForm({
  kind,
  own,
  inherited,
  inheritedFrom,
  choices,
  onChange,
  readOnly = false,
  omit = [],
}: {
  kind: ProfileUnitKind
  own: UnitSettings
  inherited: UnitSettings
  inheritedFrom: InheritedFrom | Partial<Record<SettingField, InheritedFrom>>
  choices: { screen: StoredLayout[]; ticket: StoredLayout[] }
  onChange: (next: UnitSettings) => void
  readOnly?: boolean
  /** Groups drawn elsewhere on the page (a branch machine's designs). */
  omit?: SettingField[]
}) {
  const { say } = useStaffText()
  const fromOf = (f: SettingField): InheritedFrom =>
    typeof inheritedFrom === 'string' ? inheritedFrom : (inheritedFrom[f] ?? 'default')
  // A desk display's Language is one of its design's: the one it chose, else the default.
  const customer = useCustomerLanguages()
  const layoutId = own.layoutId ?? inherited.layoutId ?? ''
  const design = layoutId ? choices.screen.find((l) => l.id === layoutId) : choices.screen.find((l) => l.isDefault)
  const deskLanguages = design ? designLanguages(design.published ?? design.draft, customer) : customer

  return (
    <div className="eq-set">
      {SETTING_FIELDS[kind].filter((f) => !omit.includes(f)).map((f) => {
        const mine = own[f] != null
        const value = (mine ? own[f] : inherited[f]) ?? blankOf(f, kind)
        const locked = readOnly
        // Changing an inherited group makes it this level's own, starting from what it showed.
        const set = (next: UnitSettings[SettingField]) => onChange({ ...own, [f]: next })
        const reset = () => {
          const rest = { ...own }
          delete rest[f]
          onChange(rest)
        }
        const source = mine ? 'eq.src.own' : fromOf(f) === 'profile' ? 'eq.src.profile' : 'eq.src.default'
        return (
          <Block
            key={f}
            title={say(f === 'voice' && kind === 'feedback' ? 'eq.sheet.language' : f === 'voice' && kind === 'desk_display' ? 'eq.sheet.languageSound' : TITLE[f])}
            source={say(source)}
            own={mine}
            readOnly={readOnly}
            onReset={reset}
          >
            {f === 'withheld' && (
              <DeskSwitches withheld={value as NonNullable<UnitSettings['withheld']>} disabled={locked} onChange={set} />
            )}
            {f === 'printer' && <Printer printer={value as PrinterSettings} disabled={locked} onChange={set} />}
            {(f === 'layoutId' || f === 'ticketLayoutId') && (
              <DesignPick
                label={say(TITLE[f])}
                value={value as string}
                layouts={f === 'layoutId' ? choices.screen : choices.ticket}
                disabled={locked}
                onChange={set}
              />
            )}
            {f === 'voice' && <Voice kind={kind} voice={value as VoiceSettings} disabled={locked} onChange={set} screenLanguages={deskLanguages} />}
            {f === 'callPage' && <CallPage callPage={value as CallPageSettings} disabled={locked} onChange={set} />}
          </Block>
        )
      })}
    </div>
  )
}

function Block({
  title,
  source,
  own,
  readOnly,
  onReset,
  children,
}: {
  title: string
  source: string
  own: boolean
  readOnly: boolean
  onReset: () => void
  children: ReactNode
}) {
  const { say } = useStaffText()
  const id = useId()
  return (
    <section className="eq-set__block" role="group" aria-labelledby={id} data-own={own || undefined}>
      <div className="eq-set__head">
        <h4 id={id}>{title}</h4>
        <Chip size="sm" variant={own ? 'primary' : 'secondary'}>
          {source}
        </Chip>
        {own && !readOnly && (
          <Button className="eq-set__own" size="sm" variant="ghost" onPress={onReset}>
            <Icon name="undo" size={14} />
            {say('eq.sheet.reset')}
          </Button>
        )}
      </div>
      <div className="eq-set__body">{children}</div>
    </section>
  )
}

/** Six switches, on when the desk offers the verb; an empty list offers all six. */
function DeskSwitches({
  withheld,
  disabled,
  onChange,
}: {
  withheld: NonNullable<UnitSettings['withheld']>
  disabled: boolean
  onChange: (next: NonNullable<UnitSettings['withheld']>) => void
}) {
  const { say } = useStaffText()
  return (
    <>
      {DESK_SWITCHES.map((s) => (
        <Check
          key={s.verb}
          label={say(s.label)}
          checked={!withheld.includes(s.verb)}
          disabled={disabled}
          onChange={(on) => onChange(withVerb(withheld, s.verb, on))}
        />
      ))}
    </>
  )
}

function Printer({
  printer,
  disabled,
  onChange,
}: {
  printer: PrinterSettings
  disabled: boolean
  onChange: (next: PrinterSettings) => void
}) {
  const { say } = useStaffText()
  const set = (patch: Partial<PrinterSettings>) => onChange({ ...printer, ...patch })
  return (
    <>
      <div className="eq-set__pair">
        <div className="field">
          <span>{say('eq.sheet.paper')}</span>
          <Segmented
            label={say('eq.sheet.paper')}
            options={(['58', '80'] as const).map((mm) => [mm, say('eq.sheet.mm', { n: mm })] as const)}
            value={String(printer.paperMm ?? 58) as '58' | '80'}
            disabled={disabled}
            onChange={(mm) => set({ paperMm: Number(mm) })}
          />
        </div>
        <label className="field">
          <span>{say('eq.sheet.copies')}</span>
          <Num min={1} max={5} value={printer.copies ?? 1} disabled={disabled} onChange={(copies) => set({ copies })} />
        </label>
      </div>
      <Check label={say('eq.sheet.cut')} checked={printer.cut ?? true} disabled={disabled} onChange={(cut) => set({ cut })} />
      <Choices
        label={say('eq.sheet.printer')}
        value={printer.render ?? 'raster'}
        disabled={disabled}
        onChange={(render) => set({ render })}
        options={(['raster', 'text'] as const).map((render) => ({
          value: render,
          label: say(render === 'raster' ? 'eq.sheet.picture' : 'eq.sheet.plain'),
          hint: say(render === 'raster' ? 'eq.sheet.pictureHint' : 'eq.sheet.plainHint'),
        }))}
      />
    </>
  )
}

/** Empty is the default design; a design nobody published says so. */
function DesignPick({
  label,
  value,
  layouts,
  disabled,
  onChange,
}: {
  label: string
  value: string
  layouts: StoredLayout[]
  disabled: boolean
  onChange: (next: string) => void
}) {
  const { say } = useStaffText()
  const named = (l: StoredLayout) => l.name || say('admin.branches.unnamedTicket')
  const options = [
    { value: '', label: say('eq.sheet.default') },
    ...layouts.map((l) => ({
      value: l.id,
      label: l.published ? named(l) : say('admin.branches.notPublishedYet', { name: named(l) }),
    })),
  ]
  // A choice this unit may no longer take (deleted, or another profile's kiosk design).
  if (value && !layouts.some((l) => l.id === value)) options.push({ value, label: say('eq.sheet.gone') })
  return <Pick label={label} value={value} disabled={disabled} onChange={onChange} options={options} />
}

function LanguagePick({
  label,
  value,
  disabled,
  onChange,
  codes,
  empty,
}: {
  label: string
  value: VoiceSettings['language']
  disabled: boolean
  onChange: (next: VoiceSettings['language']) => void
  /** The languages offered; absent is the three a call is read in. */
  codes?: readonly string[]
  /** What no language reads as. */
  empty?: string
}) {
  const { say } = useStaffText()
  const options = codes ? codes.map((c) => ({ value: c, label: nativeName(c) })) : LANGUAGES.map(([v, n]) => ({ value: v, label: n }))
  // A language the design no longer has is still shown, so the field never lies.
  if (value && !options.some((o) => o.value === value)) options.push({ value, label: say('eq.sheet.gone') })
  return (
    <label className="field">
      <span>{label}</span>
      <Pick
        label={label}
        value={value ?? ''}
        disabled={disabled}
        onChange={(next) => onChange(next || undefined)}
        options={[{ value: '', label: empty ?? say('common.notSet') }, ...options]}
      />
    </label>
  )
}

/** A feedback tablet says nothing aloud: only the language its idle screen is in. A desk display's
 *  Language is also its screen's: one of its design's, or none to follow the design. */
function Voice({
  kind,
  voice,
  disabled,
  onChange,
  screenLanguages,
}: {
  kind: ProfileUnitKind
  voice: VoiceSettings
  disabled: boolean
  onChange: (next: VoiceSettings) => void
  screenLanguages: readonly string[]
}) {
  const { say } = useStaffText()
  const set = (patch: Partial<VoiceSettings>) => onChange({ ...voice, ...patch })
  const desk = kind === 'desk_display'
  if (kind === 'feedback') {
    return (
      <LanguagePick
        label={say('eq.sheet.idleLanguage')}
        value={voice.language}
        disabled={disabled}
        onChange={(language) => onChange(language ? { language } : {})}
      />
    )
  }
  const announces = voice.announce ?? true
  const callKind = kind === 'kiosk' || kind === 'desk_display' ? kind : 'display'
  const reads = readsCalls({ ...voice, announce: true }, callKind)
  const chimes = chimesCalls({ ...voice, announce: true }, callKind)
  return (
    <>
      {desk && (
        <LanguagePick
          label={say('eq.sheet.language')}
          value={voice.language}
          codes={screenLanguages}
          empty={say('eq.sheet.followDesign')}
          disabled={disabled}
          onChange={(language) => set({ language })}
        />
      )}
      <Check label={say('eq.sheet.soundOn')} checked={announces} disabled={disabled} onChange={(announce) => set({ announce })} />
      {announces && (
        <>
          {/* Each switch pins the other, so an unset chime no longer follows the words. */}
          <Check
            label={say('eq.sheet.readCalls')}
            checked={reads}
            disabled={disabled}
            onChange={(calls) => set({ calls, chime: chimes })}
          />
          {reads && (
            <div className="eq-set__pair">
              {!desk && (
                <LanguagePick
                  label={say('eq.sheet.language')}
                  value={voice.language}
                  disabled={disabled}
                  onChange={(language) => set({ language })}
                />
              )}
              <label className="field">
                <span>{say('eq.sheet.said')}</span>
                <Pick
                  label={say('eq.sheet.said')}
                  value={String(voice.repeat ?? 2)}
                  disabled={disabled}
                  onChange={(next) => set({ repeat: Number(next) })}
                  options={[
                    { value: '1', label: say('eq.sheet.once') },
                    { value: '2', label: say('eq.sheet.twice') },
                    { value: '3', label: say('eq.sheet.threeTimes') },
                  ]}
                />
              </label>
            </div>
          )}
          <Check label={say('eq.sheet.tone')} checked={chimes} disabled={disabled} onChange={(chime) => set({ chime, calls: reads })} />
          {chimes && <ChimeSound voice={voice} disabled={disabled} onChange={(chimeAsset) => set({ chimeAsset })} />}
        </>
      )}
    </>
  )
}

/** Which sound the chime is: the built-in tone or one the organisation uploaded. */
function ChimeSound({
  voice,
  disabled,
  onChange,
}: {
  voice: VoiceSettings
  disabled: boolean
  onChange: (chimeAsset: string | undefined) => void
}) {
  const { say, lang } = useStaffText()
  const [sounds, setSounds] = useState<LayoutAsset[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState('')
  const announcer = useMemo(() => new Announcer(speechSource(), undefined, api.assetURL), [])

  useEffect(() => {
    let live = true
    api
      .sounds()
      .then((r) => live && setSounds(r.sounds))
      .catch(() => live && setSounds([]))
    return () => {
      live = false
    }
  }, [])

  async function upload(file: File) {
    setRefused('')
    if (file.size > MAX_SOUND_BYTES) {
      setRefused(explain(new ApiError(400, 'UPLOAD_TOO_LARGE', 'UPLOAD_TOO_LARGE', undefined, { mb: String(MAX_SOUND_BYTES >> 20) }), lang, 'common.unreachable'))
      return
    }
    const seconds = await soundSeconds(file)
    if (seconds !== null && seconds > MAX_CHIME_SECONDS) {
      setRefused(say('eq.sheet.soundTooLong', { n: MAX_CHIME_SECONDS }))
      return
    }
    setBusy(true)
    try {
      const sound = await api.uploadSound(file)
      setSounds((list) => [sound, ...(list ?? []).filter((s) => s.id !== sound.id)])
      onChange(sound.id)
    } catch (err) {
      setRefused(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const value = voice.chimeAsset ?? ''
  const options = [
    { value: '', label: say('eq.sheet.builtIn') },
    ...(sounds ?? []).map((s) => ({ value: s.id, label: s.filename })),
  ]
  if (value && !options.some((o) => o.value === value)) options.push({ value, label: '…' })
  return (
    <>
      <div className="eq-set__sound">
        <label className="field">
          <span>{say('eq.sheet.chimeSound')}</span>
          <Pick label={say('eq.sheet.chimeSound')} value={value} disabled={disabled || busy} onChange={(next) => onChange(next || undefined)} options={options} />
        </label>
        <Button variant="ghost" onPress={() => announcer.previewChime(voice)}>
          <Icon name="play" size={16} />
          {say('eq.sheet.play')}
        </Button>
        {!disabled && (
          <FilePicker
            accept="audio/*,.mp3,.wav,.ogg,.oga,.opus,.m4a,.aac"
            label={say('eq.sheet.uploadSound')}
            disabled={busy}
            onFile={(file) => void upload(file)}
          />
        )}
      </div>
      {refused && <span className="eq-problem">{refused}</span>}
    </>
  )
}

function CallPage({
  callPage,
  disabled,
  onChange,
}: {
  callPage: CallPageSettings
  disabled: boolean
  onChange: (next: CallPageSettings) => void
}) {
  const { say } = useStaffText()
  return (
    <>
      <Check
        label={say('eq.sheet.on')}
        checked={callPage.show ?? false}
        disabled={disabled}
        // Off with no hold chosen is the empty value, so on-then-off reads as unchanged.
        onChange={(show) => onChange(show || callPage.seconds ? { ...callPage, show } : {})}
      />
      {callPage.show && (
        <label className="field">
          <span>{say('displays.callPage.seconds')}</span>
          <Pick
            label={say('displays.callPage.seconds')}
            value={String(callPage.seconds || CALL_PAGE_DEFAULT_SECONDS)}
            disabled={disabled}
            onChange={(next) => onChange({ ...callPage, seconds: Number(next) })}
            options={CALL_PAGE_SECONDS_CHOICES.map((n) => ({ value: String(n), label: say('displays.callPage.nSeconds', { n }) }))}
          />
        </label>
      )}
    </>
  )
}
